import React, { useMemo } from 'react';
import DashboardLayout from '../../components/includes/DashboardLayout';
import { useStudents } from '../../contexts/StudentsContext';
import { useCourses } from '../../contexts/CoursesContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from "../../components/ui/select";
// import { Button } from "../../components/ui/button";
import {
  Users,
  BookOpen,
  DollarSign,
  TrendingUp,
  // Upload,
  GraduationCap,
  CheckCircle,
} from "lucide-react";

const Reports: React.FC = () => {
  const { students } = useStudents();
  const { courses } = useCourses();
  // const [selectedPeriod, setSelectedPeriod] = useState('month');

  // --- Helper Functions ---
  const getTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " ans";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " mois";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " jours";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " heures";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " minutes";
    return Math.floor(seconds) + " secondes";
  };

  // --- Calculations for Students ---
  const studentStats = useMemo(() => {
    const total = students.length;
    const active = students.filter(s => s.statut === 'actif').length;
    const graduated = students.filter(s => s.statut === 'diplome').length;
    const inactive = students.filter(s => s.statut === 'inactif' || s.statut === 'abandonne').length;

    const male = students.filter(s => s.sexe === 'M').length;
    const female = students.filter(s => s.sexe === 'F').length;

    // Level distribution
    const levels = {
      'L1': students.filter(s => s.niveau === '1').length,
      'L2': students.filter(s => s.niveau === '2').length,
      'L3': students.filter(s => s.niveau === '3').length,
      'L4': students.filter(s => s.niveau === '4').length,
    };

    // Status distribution
    const statusDist = {
      'Actif': active,
      'Diplômé': graduated,
      'Inactif/Abandon': inactive
    };

    return { active, total, male, female, levels, graduated, inactive, statusDist };
  }, [students]);

  // --- Calculations for Courses ---
  const courseStats = useMemo(() => {
    const total = courses.length;
    const active = courses.filter(c => c.status === 'actif').length;
    const inactive = courses.filter(c => c.status === 'inactif').length;
    const archived = courses.filter(c => c.status === 'archive').length;

    // Semester distribution
    const semesters = courses.reduce((acc, curr) => {
      const sems = curr.semester && curr.semester.length > 0 ? curr.semester : ['Inconnu'];
      sems.forEach(sem => {
        acc[sem] = (acc[sem] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);

    return { total, active, inactive, archived, semesters };
  }, [courses]);

  // const periods = [
  //   { id: 'week', name: 'Cette semaine' },
  //   { id: 'month', name: 'Ce mois' },
  //   { id: 'quarter', name: 'Ce trimestre' },
  //   { id: 'year', name: 'Cette année' }
  // ];

  // --- Calculations for Overview Stats ---
  const overviewStats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // New students this month
    const newStudentsMonth = students.filter(s => {
      const date = new Date(s.createdAt);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    }).length;

    // Graduated rate
    const graduatedRate = studentStats.total > 0
      ? ((studentStats.graduated / studentStats.total) * 100).toFixed(1)
      : "0.0";

    // Chart Data: Students per month (last 6 months)
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const chartData = [];

    // Generate data for last 6 months
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthIdx = d.getMonth();
      const year = d.getFullYear();

      const count = students.filter(s => {
        const sDate = new Date(s.createdAt);
        return sDate.getMonth() === monthIdx && sDate.getFullYear() === year;
      }).length;

      chartData.push({
        month: months[monthIdx],
        students: count,
        // Revenue is mocked/connected to student count for demo purposes until financial module exists
        revenue: count * 15000 // Mock: 15000 HTG per student avg
      });
    }

    // Recent Activities
    const recentStudents = students.slice(0, 5).map(s => ({
      id: `s-${s.id}`,
      type: 'enrollment',
      title: 'Nouvelle inscription',
      description: `${s.prenom} ${s.nom} - ${s.filiere || 'Ingénierie'}`,
      date: new Date(s.createdAt),
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20'
    }));

    const recentCourses = courses.slice(0, 5).map(c => ({
      id: `c-${c.id}`,
      type: 'course',
      title: 'Nouveau cours ajouté',
      description: `${c.name} - ${c.code}`,
      date: new Date(c.createdAt),
      icon: BookOpen,
      color: 'text-orange-600',
      bg: 'bg-orange-100 dark:bg-orange-900/20'
    }));

    const activities = [...recentStudents, ...recentCourses]
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 5)
      .map(a => ({
        ...a,
        time: getTimeAgo(a.date)
      }));

    return { newStudentsMonth, graduatedRate, chartData, activities };
  }, [students, courses, studentStats]);



  const overviewCards = [
    {
      title: 'Cours Actifs',
      value: courseStats.active.toString(),
      unit: '',
      icon: BookOpen,
      color: 'text-green-600',
      bg: 'bg-green-100 dark:bg-green-900/20',
      change: 'En cours',
      trend: 'neutral'
    },
    {
      title: 'Nouveaux Étudiants',
      value: overviewStats.newStudentsMonth.toString(),
      unit: '',
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20',
      change: 'Ce mois',
      trend: 'up'
    },
    {
      title: 'Taux de Diplômés',
      value: overviewStats.graduatedRate,
      unit: '%',
      icon: GraduationCap,
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/20',
      change: 'Global',
      trend: 'up'
    },
    {
      title: 'Total Inscrits',
      value: studentStats.total.toString(),
      unit: '',
      icon: CheckCircle,
      color: 'text-orange-600',
      bg: 'bg-orange-100 dark:bg-orange-900/20',
      change: 'Total',
      trend: 'up'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Rapports et Statistiques
            </h1>
            <p className="text-muted-foreground mt-1">
              Analysez les performances et les données de l'ISTMECH
            </p>
          </div>

          {/* Period Select and Export Button */}
          {/* <div className="flex items-center gap-2">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Période" />
              </SelectTrigger>
              <SelectContent>
                {periods.map((period) => (
                  <SelectItem key={period.id} value={period.id}>
                    {period.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" />
              Exporter
            </Button>
          </div> */}
        </div>

        {/* Tabs for different report views */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="students">Étudiants</TabsTrigger>
            <TabsTrigger value="courses">Cours</TabsTrigger>
            <TabsTrigger value="financial">Financier</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {overviewCards.map((stat, index) => (
                <Card key={index}>
                  <CardContent className="p-6 flex items-center justify-between space-x-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </p>
                      <div className="flex items-baseline gap-1">
                        <h3 className="text-2xl font-bold">{stat.value}</h3>
                        <span className="text-sm text-muted-foreground">{stat.unit}</span>
                      </div>
                      <div className="flex items-center mt-1">
                        <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm font-medium text-green-600">
                          {stat.change}
                        </span>
                      </div>
                    </div>
                    <div className={`p-3 rounded-full ${stat.bg} ${stat.color}`}>
                      <stat.icon className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Chart - Kept as placeholder/mock for now but labeled properly */}
              <Card>
                <CardHeader>
                  <CardTitle>Projection Revenus (Est.)</CardTitle>
                  <CardDescription>Basé sur le nombre d'étudiants actifs</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end justify-between space-x-2 pt-4">
                    {overviewStats.chartData.map((data, index) => (
                      <div key={index} className="flex flex-col items-center flex-1 group">
                        <div className="w-full bg-muted rounded-t-lg relative overflow-hidden h-48 flex items-end">
                          <div
                            className="w-full bg-blue-500 group-hover:bg-blue-600 transition-all duration-500 ease-out rounded-t-lg"
                            style={{ height: `${Math.min((data.revenue / 200000) * 100, 100)}%` }} // Normalized relative to max
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground mt-2 font-medium">
                          {data.month}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Students Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Inscriptions Nouveaux Étudiants</CardTitle>
                  <CardDescription>Derniers 6 mois</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end justify-between space-x-2 pt-4">
                    {overviewStats.chartData.map((data, index) => (
                      <div key={index} className="flex flex-col items-center flex-1 group">
                        <div className="w-full bg-muted rounded-t-lg relative overflow-hidden h-48 flex items-end">
                          <div
                            className="w-full bg-green-500 group-hover:bg-green-600 transition-all duration-500 ease-out rounded-t-lg"
                            style={{ height: `${Math.min((data.students / 10) * 100, 100)}%` }} // Normalized relative to imagined max of 10 for demo scale
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground mt-2 font-medium">
                          {data.month}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recent Activities */}
              <Card>
                <CardHeader>
                  <CardTitle>Activités Récentes</CardTitle>
                  <CardDescription>Dernières inscriptions et cours ajoutés</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {overviewStats.activities.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aucune activité récente.</p>
                    ) : (
                      overviewStats.activities.map((activity) => (
                        <div key={activity.id} className="flex items-start space-x-4">
                          <div className={`p-2 rounded-full ${activity.bg} ${activity.color}`}>
                            <activity.icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 space-y-1">
                            <p className="text-sm font-medium leading-none">
                              {activity.title}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {activity.description}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Il y a {activity.time}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Indicateurs de Performance</CardTitle>
                  <CardDescription>Progression globale</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Taux d'Activité Étudiante</span>
                      <span className="text-muted-foreground">
                        {studentStats.total > 0 ? ((studentStats.active / studentStats.total) * 100).toFixed(0) : 0}%
                      </span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${studentStats.total > 0 ? (studentStats.active / studentStats.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Taux de Cours Actifs</span>
                      <span className="text-muted-foreground">
                        {courseStats.total > 0 ? ((courseStats.active / courseStats.total) * 100).toFixed(0) : 0}%
                      </span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${courseStats.total > 0 ? (courseStats.active / courseStats.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Taux de Diplômation</span>
                      <span className="text-muted-foreground">{overviewStats.graduatedRate}%</span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${Math.min(parseFloat(overviewStats.graduatedRate), 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Inscrits</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{studentStats.total}</div>
                  <p className="text-xs text-muted-foreground">
                    Base étudiante complète
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Étudiants Actifs</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{studentStats.active}</div>
                  <p className="text-xs text-muted-foreground">
                    Actuellement en cours
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Diplômés</CardTitle>
                  <GraduationCap className="h-4 w-4 text-purple-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{studentStats.graduated}</div>
                  <p className="text-xs text-muted-foreground">
                    Anciens étudiants
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Répartition H/F</CardTitle>
                  <Users className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{studentStats.male}H / {studentStats.female}F</div>
                  <p className="text-xs text-muted-foreground">
                    {studentStats.total > 0 ? ((studentStats.female / studentStats.total) * 100).toFixed(0) : 0}% Femmes
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Répartition par Niveau (Année)</CardTitle>
                  <CardDescription>Nombre d'étudiants par année d'étude</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 pt-4">
                    {Object.entries(studentStats.levels).map(([level, count]) => (
                      <div key={level} className="flex items-center">
                        <div className="w-12 text-sm font-medium">{level}</div>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden mx-2">
                          <div
                            className="h-full bg-blue-500"
                            style={{ width: `${studentStats.total > 0 ? (count / studentStats.total) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="w-12 text-sm text-right text-muted-foreground">{count}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Répartition par Statut</CardTitle>
                  <CardDescription>Vue d'ensemble des statuts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 pt-4">
                    {Object.entries(studentStats.statusDist).map(([status, count]) => (
                      <div key={status} className="flex items-center">
                        <div className="w-32 text-sm font-medium capitalize">{status}</div>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden mx-2">
                          <div
                            className={`h-full ${status === 'Actif' ? 'bg-green-500' : status === 'Diplômé' ? 'bg-purple-500' : 'bg-slate-400'}`}
                            style={{ width: `${studentStats.total > 0 ? (count / studentStats.total) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="w-12 text-sm text-right text-muted-foreground">{count}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Cours</CardTitle>
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{courseStats.total}</div>
                  <p className="text-xs text-muted-foreground">
                    Programme complet
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Actifs</CardTitle>
                  <BookOpen className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{courseStats.active}</div>
                  <p className="text-xs text-muted-foreground">
                    Actuellement dispensés
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Inactifs</CardTitle>
                  <BookOpen className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{courseStats.inactive}</div>
                  <p className="text-xs text-muted-foreground">
                    En attente/Suspendus
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Archivés</CardTitle>
                  <CheckCircle className="h-4 w-4 text-slate-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{courseStats.archived}</div>
                  <p className="text-xs text-muted-foreground">
                    Cours clôturés
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Progression du Programme</CardTitle>
                  <CardDescription>État d'avancement global</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center">
                      <div className="w-24 text-sm font-medium">Actif</div>
                      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden mx-2">
                        <div className="h-full bg-green-500" style={{ width: `${courseStats.total > 0 ? (courseStats.active / courseStats.total) * 100 : 0}%` }} />
                      </div>
                      <div className="w-12 text-sm text-right text-muted-foreground">{courseStats.active}</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm font-medium">Inactif</div>
                      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden mx-2">
                        <div className="h-full bg-blue-500" style={{ width: `${courseStats.total > 0 ? (courseStats.inactive / courseStats.total) * 100 : 0}%` }} />
                      </div>
                      <div className="w-12 text-sm text-right text-muted-foreground">{courseStats.inactive}</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm font-medium">Archivé</div>
                      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden mx-2">
                        <div className="h-full bg-slate-500" style={{ width: `${courseStats.total > 0 ? (courseStats.archived / courseStats.total) * 100 : 0}%` }} />
                      </div>
                      <div className="w-12 text-sm text-right text-muted-foreground">{courseStats.archived}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Répartition par Semestre</CardTitle>
                  <CardDescription>Nombre de cours par semestre</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 pt-4 h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                    {Object.entries(courseStats.semesters).sort().map(([sem, count]) => (
                      <div key={sem} className="flex items-center">
                        <div className="w-24 text-sm font-medium">
                          {sem === '1' ? '1er Semestre' : sem === '2' ? '2ème Semestre' : `Semestre ${sem}`}
                        </div>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden mx-2">
                          <div
                            className="h-full bg-orange-500"
                            style={{ width: `${courseStats.total > 0 ? (count / courseStats.total) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="w-12 text-sm text-right text-muted-foreground">{count}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial">
            <div className="flex flex-col items-center justify-center h-[400px] space-y-4 border rounded-lg bg-card/50">
              <div className="p-4 rounded-full bg-muted">
                <DollarSign className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold">Bientôt Disponible</h3>
              <p className="text-muted-foreground text-center max-w-sm px-4">
                Le module financier est en cours de développement. Vous pourrez bientôt consulter les rapports financiers détaillés ici.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
